/* eslint-disable max-lines */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";

import { useSelector, useDispatch } from "react-redux";

import { getWheelPoint, getPlayerCash } from "../store/slices/user";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CheckinExperience = ({ currentUser, showToast = () => {} }) => {
  const dispatch = useDispatch();
  const { singleUsers } = useSelector((state) => state.user || {});
  const user = singleUsers && singleUsers.length ? singleUsers[0] : null;
  const [checkinActivity, setCheckinActivity] = useState(null);
  const [checkinModalOpen, setCheckinModalOpen] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinError, setCheckinError] = useState(null);
  const [checkinSubmitting, setCheckinSubmitting] = useState(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchCheckinActivity = useCallback(
    async (options = {}) => {
      if (!API_BASE_URL) return null;
      const { signal, silent } = options;
      if (!silent && isMountedRef.current) {
        setCheckinLoading(true);
      }
      if (isMountedRef.current) {
        setCheckinError(null);
      }
      try {
        const query = currentUser?.id ? `?userId=${currentUser.id}` : "";
        const response = await fetch(`${API_BASE_URL}/checkin/active${query}`, {
          signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || "ไม่สามารถโหลดกิจกรรมเช็คอินได้");
        }
        if (!signal?.aborted && isMountedRef.current) {
          setCheckinActivity(payload?.data || null);
        }
        return payload?.data || null;
      } catch (error) {
        if (error.name === "AbortError" || signal?.aborted) {
          return null;
        }
        if (!signal?.aborted && isMountedRef.current) {
          setCheckinActivity(null);
          setCheckinError(error.message || "เกิดข้อผิดพลาด");
        }
        return null;
      } finally {
        if (!silent && isMountedRef.current) {
          setCheckinLoading(false);
        }
      }
    },
    [currentUser?.id],
  );

  useEffect(() => {
    if (!API_BASE_URL) return;
    const controller = new AbortController();
    fetchCheckinActivity({ signal: controller.signal });
    return () => {
      controller.abort();
    };
  }, [fetchCheckinActivity]);

  const buildCheckinImageUrl = (path) => {
    if (!path) {
      return "https://placehold.co/80x80?text=Check-in";
    }
    if (/^https?:/i.test(path)) {
      return path;
    }
    const base = (API_BASE_URL || "").replace(/\/$/, "");
    return `${base}${path}`;
  };

  const sortedCheckinRewards = useMemo(() => {
    if (!checkinActivity?.rewards) return [];
    return [...checkinActivity.rewards].sort(
      (a, b) => (a.dayNumber || 0) - (b.dayNumber || 0),
    );
  }, [checkinActivity]);

  const totalCheckinRewards = useMemo(() => {
    if (!sortedCheckinRewards.length) return { cash: 0, diamond: 0 };
    return sortedCheckinRewards.reduce(
      (acc, reward) => {
        const amount = Number(reward.rewardAmount) || 0;
        if (reward.rewardType === "cashWallet") {
          acc.cash += amount;
        } else {
          acc.diamond += amount;
        }
        return acc;
      },
      { cash: 0, diamond: 0 },
    );
  }, [sortedCheckinRewards]);

  const progressState = useMemo(() => {
    if (!checkinActivity) return {};
    const base = checkinActivity.progress || checkinActivity.userProgress || {};
    const normalizedClaimed = Array.isArray(base.claimedDays)
      ? base.claimedDays.map((day) => Number(day) || 0).filter((day) => day > 0)
      : [];
    return {
      ...base,
      currentDay: Number(base.currentDay) || 0,
      streakCount: Number(base.streakCount) || 0,
      claimedDays: normalizedClaimed,
      completed: Boolean(base.completed),
      lastCheckinDate: base.lastCheckinDate || null,
    };
  }, [checkinActivity]);

  const checkinEligibility = useMemo(() => {
    const base = checkinActivity?.eligibility || {};
    const minDailyDepositAmount = Number(base.minDailyDepositAmount) || 100;
    const todayDepositAmount = Number(base.todayDepositAmount) || 0;
    const remainingDepositAmount = Math.max(
      Number(base.remainingDepositAmount) ||
        minDailyDepositAmount - todayDepositAmount,
      0,
    );

    return {
      minDailyDepositAmount,
      todayDepositAmount,
      remainingDepositAmount,
      canClaim: Boolean(base.canClaim) || remainingDepositAmount <= 0,
    };
  }, [checkinActivity]);

  const checkinDepositWarning = useMemo(() => {
    if (!currentUser || checkinEligibility.canClaim) {
      return null;
    }

    return `ต้องฝากวันนี้อย่างน้อย ${checkinEligibility.minDailyDepositAmount.toLocaleString()} บาทก่อนรับกิจกรรมเช็คอิน ตอนนี้ฝากแล้ว ${checkinEligibility.todayDepositAmount.toLocaleString()} บาท`;
  }, [checkinEligibility, currentUser]);

  const claimedDayList = useMemo(() => {
    return Array.isArray(progressState.claimedDays)
      ? progressState.claimedDays
      : [];
  }, [progressState.claimedDays]);

  const claimedDaysCount = useMemo(() => {
    if (claimedDayList.length) {
      return claimedDayList.length;
    }
    const value =
      progressState.completedDays ??
      progressState.currentDay ??
      progressState.checkedDays ??
      0;
    return Number(value) || 0;
  }, [
    claimedDayList,
    progressState.completedDays,
    progressState.currentDay,
    progressState.checkedDays,
  ]);

  const focusDayNumber = useMemo(() => {
    const totalDays =
      sortedCheckinRewards.length || checkinActivity?.totalDays || 1;
    if (!totalDays) return 1;
    if (progressState.completed) {
      return Math.min(progressState.currentDay || totalDays, totalDays);
    }
    const baseDay = Math.max(
      claimedDaysCount,
      progressState.currentDay || claimedDaysCount,
    );
    const nextDay = baseDay + 1;
    return Math.min(Math.max(nextDay, 1), totalDays);
  }, [
    claimedDaysCount,
    progressState.completed,
    progressState.currentDay,
    sortedCheckinRewards,
    checkinActivity,
  ]);

  const totalConfiguredDays =
    sortedCheckinRewards.length || checkinActivity?.totalDays || 0;

  const completedDaysDisplay = Math.min(
    progressState.currentDay || claimedDaysCount,
    totalConfiguredDays || 0,
  );

  const formatCheckinDate = useCallback(
    (dayNumber) => {
      if (!checkinActivity?.startDate) return null;
      const base = new Date(checkinActivity.startDate);
      if (Number.isNaN(base.getTime())) return null;
      const target = new Date(base);
      target.setDate(target.getDate() + (dayNumber - 1));
      const day = `${target.getDate()}`.padStart(2, "0");
      const month = `${target.getMonth() + 1}`.padStart(2, "0");
      const year = target.getFullYear();
      return `${day}-${month}-${year}`;
    },
    [checkinActivity?.startDate],
  );

  const getDayStatus = useCallback(
    (dayNumber) => {
      if (claimedDayList.includes(dayNumber)) return "completed";
      if (!progressState.completed && dayNumber === focusDayNumber)
        return "current";
      if (progressState.completed && dayNumber <= progressState.currentDay)
        return "completed";
      return "upcoming";
    },
    [
      claimedDayList,
      focusDayNumber,
      progressState.completed,
      progressState.currentDay,
    ],
  );

  const describeReward = useCallback((reward) => {
    const amount = Number(reward.rewardAmount || 0).toLocaleString();
    return reward.rewardType === "cashWallet"
      ? `${amount} ฿`
      : `${amount} เพชร`;
  }, []);

  const shouldRenderCheckinWidget = Boolean(checkinActivity);
  const checkinThemeColor = checkinActivity?.themeColor || "#0e6c5e";

  const handleCheckinButtonClick = () => {
    if (checkinSubmitting) return;
    if (!shouldRenderCheckinWidget) return;
    setCheckinModalOpen(true);
  };

  const closeCheckinModal = () => {
    if (!checkinSubmitting) {
      setCheckinModalOpen(false);
    }
  };

  const handleStartCheckin = async () => {
    if (!checkinActivity?.id) return;
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบเพื่อบันทึกเช็คอิน", "warning");
      return;
    }
    if (!checkinEligibility.canClaim) {
      showToast(
        checkinDepositWarning || "ยอดฝากวันนี้ยังไม่ถึง 100 บาท",
        "warning",
      );
      return;
    }
    if (checkinSubmitting) return;

    if (isMountedRef.current) {
      setCheckinSubmitting(true);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/checkin/active/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: currentUser.id,
          eventId: checkinActivity.id,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "ไม่สามารถบันทึกการเช็คอินได้");
      }
      if (isMountedRef.current) {
        showToast(payload?.message || "เช็คอินสำเร็จ", "info");
        dispatch(getWheelPoint(user));
        dispatch(getPlayerCash(user));
        setCheckinModalOpen(false);
      }
      await fetchCheckinActivity({ silent: true });
    } catch (error) {
      if (isMountedRef.current) {
        showToast(error.message || "ระบบเช็คอินไม่พร้อมใช้งาน", "error");
      }
    } finally {
      if (isMountedRef.current) {
        setCheckinSubmitting(false);
      }
    }
  };

  if (!shouldRenderCheckinWidget) {
    return null;
  }

  return (
    <>
      <div
        className="checkin-trigger-wrapper"
        style={{
          position: "fixed",
          top: "calc(36% + 8px)",
          right: 0,
          transform: "translateY(-50%)",
          zIndex: 1000,
          background:
            "linear-gradient(135deg, rgba(14, 108, 94, 0.95) 0%, rgba(9, 77, 68, 0.9) 100%)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: "8px 6px 10px",
          borderTopLeftRadius: "14px",
          borderBottomLeftRadius: "14px",
          width: "62px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "6px",
          userSelect: "none",
          boxShadow:
            "0 8px 32px rgba(14, 108, 94, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
          transition:
            "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-50%) translateX(-4px)";
          e.currentTarget.style.boxShadow =
            "0 12px 40px rgba(14, 108, 94, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(-50%) translateX(0)";
          e.currentTarget.style.boxShadow =
            "0 8px 32px rgba(14, 108, 94, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
        }}
      >
        {/* Decorative glow */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "-20px",
            width: "40px",
            height: "60px",
            background: `radial-gradient(ellipse, ${checkinThemeColor}40 0%, transparent 70%)`,
            transform: "translateY(-50%)",
            pointerEvents: "none",
            filter: "blur(8px)",
          }}
        />

        <button
          type="button"
          onClick={handleCheckinButtonClick}
          disabled={checkinSubmitting}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: checkinSubmitting ? "not-allowed" : "pointer",
            opacity: checkinSubmitting ? 0.7 : 1,
            transition:
              "transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease",
            position: "relative",
          }}
          onMouseEnter={(e) => {
            if (!checkinSubmitting) {
              e.currentTarget.style.transform = "scale(1.08)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
          aria-label="เปิดกิจกรรมเช็คอินรับโชค"
        >
          {/* Icon container with gradient border */}
          <div
            style={{
              position: "relative",
              width: "50px",
              height: "50px",
              borderRadius: "14px",
              background: `linear-gradient(145deg, ${checkinThemeColor}, ${checkinThemeColor}88)`,
              padding: "3px",
              boxShadow: `0 4px 20px ${checkinThemeColor}40, 0 2px 8px rgba(0, 0, 0, 0.3)`,
            }}
          >
            <img
              src={buildCheckinImageUrl(checkinActivity?.iconUrl)}
              alt="checkin-modal-trigger"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "13px",
                display: "block",
              }}
            />
            {/* Shine overlay */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: "16px",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, transparent 100%)",
                pointerEvents: "none",
              }}
            />
          </div>

          {/* Notification badge (optional pulse) */}
          <div
            style={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #b7e3d8, #0e6c5e)",
              border: "2px solid rgba(14, 108, 94, 0.9)",
              boxShadow: "0 2px 8px rgba(183, 227, 216, 0.6)",
              animation: "checkinPulse 2s ease-in-out infinite",
            }}
          />
        </button>

        {(checkinActivity?.subtitle || checkinActivity?.title) && (
          <span
            style={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "rgba(255,255,255,0.95)",
              textAlign: "center",
              lineHeight: 1.3,
              letterSpacing: "0.02em",
              width: "100%",
              padding: "0 2px",
              textShadow: "0 1px 4px rgba(0, 0, 0, 0.5)",
            }}
          >
            {checkinActivity?.subtitle || "เช็คอิน"}
          </span>
        )}
      </div>

      <style>
        {`
          @keyframes checkinPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.15); opacity: 0.85; }
          }
          
          .checkin-trigger-wrapper::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            border-radius: 16px 16px 0 0;
          }
        `}
      </style>

      {checkinError && !checkinLoading && !checkinActivity && (
        <div
          style={{
            marginTop: "20px",
            padding: "12px 16px",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.08)",
            color: "#ffcd94",
            fontSize: "0.9rem",
          }}
        >
          ⚠️ {checkinError}
        </div>
      )}

      {checkinModalOpen && (
        <div className="checkin-modal-overlay" onClick={closeCheckinModal}>
          <div
            className="checkin-modal-container"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Decorative elements */}
            <div className="checkin-modal-decor">
              <div className="checkin-decor-circle checkin-decor-circle--1" />
              <div className="checkin-decor-circle checkin-decor-circle--2" />
              <div className="checkin-decor-circle checkin-decor-circle--3" />
            </div>

            {/* Close button */}
            <button
              onClick={closeCheckinModal}
              disabled={checkinSubmitting}
              className="checkin-modal-close"
              aria-label="ปิด"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 1L13 13M1 13L13 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Header section */}
            <div className="checkin-modal-header">
              <div
                className="checkin-header-icon"
                style={{
                  background: `linear-gradient(135deg, ${checkinThemeColor}, ${checkinThemeColor}88)`,
                }}
              >
                <img
                  src={buildCheckinImageUrl(checkinActivity?.iconUrl)}
                  alt="daily-checkin"
                />
                <div
                  className="checkin-header-icon__glow"
                  style={{ background: checkinThemeColor }}
                />
              </div>
              <div className="checkin-header-content">
                <span className="checkin-header-badge">
                  <span
                    className="checkin-badge-dot"
                    style={{ background: checkinThemeColor }}
                  />
                  {checkinActivity?.subtitle || "🎁 7 วันหรรษา"}
                </span>
                <h2 className="checkin-header-title">
                  {checkinActivity?.title || "เช็คอินรับโชค"}
                </h2>
                <p className="checkin-header-desc">
                  {checkinActivity?.description ||
                    "เช็คอินสะสมทุกวันเพื่อปลดล็อกรางวัลพิเศษทั้งเพชรและเงินสด"}
                </p>
              </div>
            </div>

            {/* Progress section */}
            <div className="checkin-progress-section">
              <div className="checkin-progress-header">
                <div className="checkin-progress-info">
                  <span className="checkin-progress-label">ความคืบหน้า</span>
                  <span className="checkin-progress-count">
                    <strong>{completedDaysDisplay}</strong>
                    <span>/ {totalConfiguredDays} วัน</span>
                  </span>
                </div>
                <div className="checkin-progress-percentage">
                  {totalConfiguredDays > 0
                    ? Math.round(
                        (completedDaysDisplay / totalConfiguredDays) * 100,
                      )
                    : 0}
                  %
                </div>
              </div>
              <div className="checkin-progress-bar">
                <div
                  className="checkin-progress-fill"
                  style={{
                    width: `${
                      totalConfiguredDays > 0
                        ? (completedDaysDisplay / totalConfiguredDays) * 100
                        : 0
                    }%`,
                    background: `linear-gradient(90deg, ${checkinThemeColor}, ${checkinThemeColor}cc)`,
                  }}
                >
                  <div className="checkin-progress-shine" />
                </div>
              </div>
            </div>

            {/* Stats cards */}
            <div className="checkin-stats-row">
              <div className="checkin-stat-card">
                <div className="checkin-stat-icon">💎</div>
                <div className="checkin-stat-content">
                  <span className="checkin-stat-label">รวมเพชร</span>
                  <strong className="checkin-stat-value">
                    {totalCheckinRewards.diamond.toLocaleString()} เพชร
                  </strong>
                </div>
              </div>
              <div className="checkin-stat-card">
                <div className="checkin-stat-icon">💰</div>
                <div className="checkin-stat-content">
                  <span className="checkin-stat-label">รวมเงินสด</span>
                  <strong className="checkin-stat-value">
                    {totalCheckinRewards.cash.toLocaleString()} ฿
                  </strong>
                </div>
              </div>
            </div>

            {/* Error message */}
            {!checkinLoading && checkinError && (
              <div className="checkin-error-banner">
                <span>⚠️</span>
                <span>{checkinError}</span>
              </div>
            )}

            {/* Days grid */}
            <div className="checkin-days-section">
              <h3 className="checkin-days-title">รางวัลประจำวัน</h3>
              <div className="checkin-days-grid">
                {sortedCheckinRewards.map((reward, index) => {
                  const status = getDayStatus(reward.dayNumber);
                  const isSpecialDay =
                    reward.dayNumber === 7 || reward.rewardAmount >= 100;
                  return (
                    <div
                      key={`checkin-card-${reward.dayNumber}`}
                      className={`checkin-day-card checkin-day-card--${status} ${
                        isSpecialDay ? "checkin-day-card--special" : ""
                      }`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      {status === "completed" && (
                        <div className="checkin-day-checkmark">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <circle cx="10" cy="10" r="10" fill="#22c55e" />
                            <path
                              d="M6 10L9 13L14 7"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                      {status === "current" && (
                        <div className="checkin-day-current-badge">
                          <span>วันนี้</span>
                        </div>
                      )}
                      <div className="checkin-day-number">
                        Day {reward.dayNumber}
                      </div>
                      <div className="checkin-day-reward-icon">
                        {reward.rewardType === "cashWallet" ? "💸" : "💎"}
                      </div>
                      <div className="checkin-day-reward-amount">
                        {describeReward(reward)}
                      </div>
                      <div className="checkin-day-date">
                        {formatCheckinDate(reward.dayNumber) ||
                          `Day ${reward.dayNumber}`}
                      </div>
                      {isSpecialDay && (
                        <div
                          className="checkin-day-special-glow"
                          style={{ background: checkinThemeColor }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA Button */}
            <div className="checkin-cta-section">
              {checkinDepositWarning && (
                <div className="checkin-error-banner">
                  <span>⚠️</span>
                  <span>
                    {checkinDepositWarning}
                    {checkinEligibility.remainingDepositAmount > 0
                      ? ` ฝากเพิ่มอีก ${checkinEligibility.remainingDepositAmount.toLocaleString()} บาท`
                      : ""}
                  </span>
                </div>
              )}
              <button
                onClick={handleStartCheckin}
                disabled={
                  checkinSubmitting ||
                  checkinLoading ||
                  !checkinActivity ||
                  (!!currentUser && !checkinEligibility.canClaim)
                }
                className="checkin-cta-button"
                style={{
                  background: `linear-gradient(135deg, ${checkinThemeColor}, ${checkinThemeColor}dd)`,
                  opacity:
                    checkinSubmitting ||
                    checkinLoading ||
                    !checkinActivity ||
                    (!!currentUser && !checkinEligibility.canClaim)
                      ? 0.6
                      : 1,
                }}
              >
                {checkinSubmitting ? (
                  <>
                    <span className="checkin-cta-spinner" />
                    <span>กำลังเช็คอิน...</span>
                  </>
                ) : (
                  <>
                    <span className="checkin-cta-icon">🎯</span>
                    <span>
                      {currentUser && !checkinEligibility.canClaim
                        ? `ต้องฝากเพิ่ม ${checkinEligibility.remainingDepositAmount.toLocaleString()} บาท`
                        : checkinActivity?.buttonText || "เช็คอินรับรางวัล"}
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* Loading state */}
            {checkinLoading && (
              <div className="checkin-loading-overlay">
                <div
                  className="checkin-loading-spinner"
                  style={{ borderTopColor: checkinThemeColor }}
                />
                <span>กำลังโหลด...</span>
              </div>
            )}

            <style>
              {`
                @keyframes fadeInUp {
                  from {
                    opacity: 0;
                    transform: translateY(30px) scale(0.95);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                }
                @keyframes fadeIn {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                @keyframes pulse {
                  0%, 100% { transform: scale(1); opacity: 0.8; }
                  50% { transform: scale(1.05); opacity: 1; }
                }
                @keyframes shimmer {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(100%); }
                }
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
                @keyframes float {
                  0%, 100% { transform: translateY(0) rotate(0deg); }
                  50% { transform: translateY(-10px) rotate(5deg); }
                }
                @keyframes cardAppear {
                  from {
                    opacity: 0;
                    transform: translateY(20px) scale(0.9);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                }
                @keyframes glowPulse {
                  0%, 100% { opacity: 0.4; transform: scale(1); }
                  50% { opacity: 0.8; transform: scale(1.2); }
                }

                .checkin-modal-overlay {
                  position: fixed;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  background: rgba(0, 0, 0, 0.85);
                  backdrop-filter: blur(8px);
                  -webkit-backdrop-filter: blur(8px);
                  z-index: 1500;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 16px;
                  animation: fadeIn 0.3s ease;
                }

                .checkin-modal-container {
                  width: 100%;
                  max-width: 680px;
                  max-height: 90vh;
                  overflow-y: auto;
                  overflow-x: hidden;
                  background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 251, 250, 0.95) 100%);
                  border-radius: 24px;
                  padding: 28px;
                  position: relative;
                  color: #1f2937;
                  box-shadow: 
                    0 0 0 2px rgba(14, 108, 94, 0.2),
                    0 25px 50px -12px rgba(14, 108, 94, 0.3),
                    0 0 100px rgba(14, 108, 94, 0.15);
                  animation: fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .checkin-modal-container::-webkit-scrollbar {
                  width: 6px;
                }
                .checkin-modal-container::-webkit-scrollbar-track {
                  background: rgba(14, 108, 94, 0.05);
                  border-radius: 3px;
                }
                .checkin-modal-container::-webkit-scrollbar-thumb {
                  background: rgba(14, 108, 94, 0.3);
                  border-radius: 3px;
                }
                .checkin-modal-container::-webkit-scrollbar-thumb:hover {
                  background: rgba(14, 108, 94, 0.5);
                }

                .checkin-modal-decor {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  pointer-events: none;
                  overflow: hidden;
                  border-radius: 24px;
                }

                .checkin-decor-circle {
                  position: absolute;
                  border-radius: 50%;
                  filter: blur(60px);
                  opacity: 0.15;
                }
                .checkin-decor-circle--1 {
                  width: 300px;
                  height: 300px;
                  background: #0e6c5e;
                  top: -100px;
                  right: -50px;
                }
                .checkin-decor-circle--2 {
                  width: 200px;
                  height: 200px;
                  background: #b7e3d8;
                  bottom: 50px;
                  left: -50px;
                }
                .checkin-decor-circle--3 {
                  width: 150px;
                  height: 150px;
                  background: #0b5b50;
                  bottom: -50px;
                  right: 30%;
                }

                .checkin-modal-close {
                  position: absolute;
                  right: 16px;
                  top: 16px;
                  width: 36px;
                  height: 36px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background: rgba(14, 108, 94, 0.08);
                  border: 1px solid rgba(14, 108, 94, 0.2);
                  border-radius: 12px;
                  color: rgba(14, 108, 94, 0.7);
                  cursor: pointer;
                  transition: all 0.2s ease;
                  z-index: 10;
                }
                .checkin-modal-close:hover {
                  background: rgba(14, 108, 94, 0.15);
                  color: #0e6c5e;
                  transform: rotate(90deg);
                }
                .checkin-modal-close:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }

                .checkin-modal-header {
                  display: flex;
                  gap: 20px;
                  align-items: flex-start;
                  margin-bottom: 24px;
                  position: relative;
                  z-index: 1;
                }

                .checkin-header-icon {
                  width: 80px;
                  height: 80px;
                  border-radius: 20px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                  position: relative;
                  overflow: hidden;
                  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                }
                .checkin-header-icon img {
                  width: 56px;
                  height: 56px;
                  object-fit: cover;
                  border-radius: 14px;
                  position: relative;
                  z-index: 1;
                  animation: float 3s ease-in-out infinite;
                }
                .checkin-header-icon__glow {
                  position: absolute;
                  width: 60%;
                  height: 60%;
                  border-radius: 50%;
                  filter: blur(20px);
                  opacity: 0.6;
                  animation: glowPulse 2s ease-in-out infinite;
                }

                .checkin-header-content {
                  flex: 1;
                  min-width: 0;
                }

                .checkin-header-badge {
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                  background: rgba(14, 108, 94, 0.1);
                  padding: 6px 12px;
                  border-radius: 20px;
                  font-size: 0.8rem;
                  color: #0e6c5e;
                  margin-bottom: 8px;
                  font-weight: 600;
                }
                .checkin-badge-dot {
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  animation: pulse 2s ease-in-out infinite;
                }

                .checkin-header-title {
                  margin: 0 0 8px;
                  font-size: 1.75rem;
                  font-weight: 700;
                  background: linear-gradient(135deg, #0e6c5e 0%, #0b5b50 100%);
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                  background-clip: text;
                }

                .checkin-header-desc {
                  margin: 0;
                  font-size: 0.9rem;
                  color: rgba(31, 41, 55, 0.7);
                  line-height: 1.5;
                }

                .checkin-progress-section {
                  background: rgba(233, 247, 244, 0.6);
                  border: 2px solid rgba(14, 108, 94, 0.2);
                  border-radius: 16px;
                  padding: 16px 20px;
                  margin-bottom: 16px;
                  position: relative;
                  z-index: 1;
                }

                .checkin-progress-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                }

                .checkin-progress-info {
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                }

                .checkin-progress-label {
                  font-size: 0.75rem;
                  color: rgba(14, 108, 94, 0.7);
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                  font-weight: 600;
                }

                .checkin-progress-count {
                  display: flex;
                  align-items: baseline;
                  gap: 4px;
                }
                .checkin-progress-count strong {
                  font-size: 1.5rem;
                  font-weight: 700;
                  color: #0e6c5e;
                }
                .checkin-progress-count span {
                  font-size: 0.9rem;
                  color: rgba(31, 41, 55, 0.6);
                }

                .checkin-progress-percentage {
                  font-size: 1.25rem;
                  font-weight: 700;
                  color: #0e6c5e;
                }

                .checkin-progress-bar {
                  height: 10px;
                  background: rgba(14, 108, 94, 0.1);
                  border-radius: 10px;
                  overflow: hidden;
                }

                .checkin-progress-fill {
                  height: 100%;
                  border-radius: 10px;
                  position: relative;
                  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                  overflow: hidden;
                }

                .checkin-progress-shine {
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 50%;
                  height: 100%;
                  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
                  animation: shimmer 2s infinite;
                }

                .checkin-stats-row {
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  gap: 12px;
                  margin-bottom: 20px;
                  position: relative;
                  z-index: 1;
                }

                .checkin-stat-card {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  background: rgba(233, 247, 244, 0.6);
                  border: 2px solid rgba(14, 108, 94, 0.2);
                  border-radius: 14px;
                  padding: 14px 16px;
                  transition: all 0.2s ease;
                }
                .checkin-stat-card:hover {
                  background: rgba(233, 247, 244, 0.9);
                  border-color: rgba(14, 108, 94, 0.3);
                  transform: translateY(-2px);
                  box-shadow: 0 4px 12px rgba(14, 108, 94, 0.15);
                }

                .checkin-stat-icon {
                  font-size: 1.75rem;
                }

                .checkin-stat-content {
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                }

                .checkin-stat-label {
                  font-size: 0.75rem;
                  color: rgba(14, 108, 94, 0.7);
                  font-weight: 600;
                }

                .checkin-stat-value {
                  font-size: 1.1rem;
                  font-weight: 700;
                  color: #0e6c5e;
                }

                .checkin-error-banner {
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  background: rgba(239, 68, 68, 0.15);
                  border: 1px solid rgba(239, 68, 68, 0.3);
                  border-radius: 12px;
                  padding: 12px 16px;
                  margin-bottom: 16px;
                  color: #fca5a5;
                  font-size: 0.9rem;
                }

                .checkin-days-section {
                  position: relative;
                  z-index: 1;
                }

                .checkin-days-title {
                  margin: 0 0 16px;
                  font-size: 1rem;
                  font-weight: 700;
                  color: #0e6c5e;
                }

                .checkin-days-grid {
                  display: grid;
                  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
                  gap: 10px;
                }

                .checkin-day-card {
                  position: relative;
                  background: #ffffff;
                  border: 2px solid rgba(14, 108, 94, 0.15);
                  border-radius: 16px;
                  padding: 14px 10px;
                  text-align: center;
                  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                  animation: cardAppear 0.4s ease forwards;
                  opacity: 0;
                  overflow: hidden;
                }
                .checkin-day-card:hover {
                  transform: translateY(-4px) scale(1.02);
                  background: rgba(233, 247, 244, 0.5);
                  border-color: rgba(14, 108, 94, 0.3);
                  box-shadow: 0 8px 20px rgba(14, 108, 94, 0.15);
                }

                .checkin-day-card--completed {
                  background: rgba(183, 227, 216, 0.3);
                  border-color: rgba(14, 108, 94, 0.5);
                }

                .checkin-day-card--current {
                  background: rgba(14, 108, 94, 0.1);
                  border-color: #0e6c5e;
                  box-shadow: 0 0 20px rgba(14, 108, 94, 0.3), 0 4px 12px rgba(14, 108, 94, 0.2);
                }

                .checkin-day-card--upcoming {
                  opacity: 0.7;
                }

                .checkin-day-card--special {
                  background: linear-gradient(135deg, rgba(14, 108, 94, 0.15), rgba(183, 227, 216, 0.15));
                  border-color: rgba(14, 108, 94, 0.4);
                }

                .checkin-day-checkmark {
                  position: absolute;
                  top: 8px;
                  right: 8px;
                }

                .checkin-day-current-badge {
                  position: absolute;
                  top: -1px;
                  left: 50%;
                  transform: translateX(-50%);
                  background: linear-gradient(135deg, #0e6c5e, #0b5b50);
                  color: #fff;
                  font-size: 0.65rem;
                  font-weight: 700;
                  padding: 3px 10px;
                  border-radius: 0 0 8px 8px;
                  text-transform: uppercase;
                }

                .checkin-day-number {
                  font-size: 0.7rem;
                  font-weight: 600;
                  color: rgba(14, 108, 94, 0.7);
                  margin-bottom: 8px;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                }

                .checkin-day-reward-icon {
                  font-size: 2rem;
                  margin-bottom: 6px;
                }

                .checkin-day-reward-amount {
                  font-size: 1rem;
                  font-weight: 700;
                  color: #0e6c5e;
                  margin-bottom: 4px;
                }

                .checkin-day-date {
                  font-size: 0.7rem;
                  color: rgba(31, 41, 55, 0.5);
                }

                .checkin-day-special-glow {
                  position: absolute;
                  bottom: -30px;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 60px;
                  height: 60px;
                  border-radius: 50%;
                  filter: blur(25px);
                  opacity: 0.3;
                  pointer-events: none;
                }

                .checkin-cta-section {
                  margin-top: 24px;
                  position: relative;
                  z-index: 1;
                }

                .checkin-cta-button {
                  width: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 10px;
                  padding: 16px 24px;
                  border: none;
                  border-radius: 16px;
                  font-size: 1.1rem;
                  font-weight: 700;
                  color: #fff;
                  cursor: pointer;
                  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
                  position: relative;
                  overflow: hidden;
                }
                .checkin-cta-button:hover:not(:disabled) {
                  transform: translateY(-2px);
                  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
                }
                .checkin-cta-button:active:not(:disabled) {
                  transform: translateY(0);
                }
                .checkin-cta-button:disabled {
                  cursor: not-allowed;
                }

                .checkin-cta-icon {
                  font-size: 1.3rem;
                }

                .checkin-cta-spinner {
                  width: 20px;
                  height: 20px;
                  border: 2px solid rgba(255, 255, 255, 0.3);
                  border-top-color: #fff;
                  border-radius: 50%;
                  animation: spin 0.8s linear infinite;
                }

                .checkin-loading-overlay {
                  position: absolute;
                  inset: 0;
                  background: rgba(0, 0, 0, 0.7);
                  backdrop-filter: blur(4px);
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  gap: 16px;
                  border-radius: 24px;
                  z-index: 100;
                }

                .checkin-loading-spinner {
                  width: 40px;
                  height: 40px;
                  border: 3px solid rgba(255, 255, 255, 0.2);
                  border-radius: 50%;
                  animation: spin 1s linear infinite;
                }

                @media (max-width: 640px) {
                  .checkin-modal-container {
                    padding: 20px;
                    border-radius: 20px;
                  }

                  .checkin-modal-header {
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                  }

                  .checkin-header-icon {
                    width: 72px;
                    height: 72px;
                  }
                  .checkin-header-icon img {
                    width: 48px;
                    height: 48px;
                  }

                  .checkin-header-title {
                    font-size: 1.4rem;
                  }

                  .checkin-stats-row {
                    grid-template-columns: 1fr;
                  }

                  .checkin-days-grid {
                    grid-template-columns: repeat(3, 1fr);
                    gap: 8px;
                  }

                  .checkin-day-card {
                    padding: 12px 8px;
                  }

                  .checkin-day-reward-icon {
                    font-size: 1.5rem;
                  }

                  .checkin-day-reward-amount {
                    font-size: 0.9rem;
                  }
                }
              `}
            </style>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckinExperience;
